import { useCallback } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../../../store/store';

/** Attach/replace-product mode — in this mode the live cart holds the
 *  WHOLE hydrated order (products carry their order-product id), so add-to-cart
 *  guards (duplicate room, pax caps, "order already saved") must evaluate only
 *  the agent's NEW selection: top-level products without an id, plus new
 *  subProducts (no id) added under existing order products. */
export const filterNewCartProducts = (products?: any[]): any[] =>
  (products ?? [])
    .map((p: any) =>
      p?.id
        ? {
            ...p,
            subProducts: (p.subProducts ?? []).filter((sp: any) => !sp.id),
          }
        : p
    )
    .filter((p: any) => !p?.id || p.subProducts?.length);

/** In attach mode only the DELTA is removable: a node the agent just picked
 *  carries no order-product id. Everything already booked — including the
 *  product being replaced — is locked, because dropping it from the cart
 *  would only hide it (the delta submit never touches sibling products) while
 *  corrupting the displayed order. Removing a booked product is a Backoffice
 *  operation, not a cart one. */
export const isAttachNodeRemovable = (node: any, isAttachMode: boolean) =>
  !isAttachMode || !node?.id;

const useAttachMode = () => {
  const attachToOrder = useSelector(
    (state: RootState) => state.shoppingCartV2.attachToOrder
  );
  const isAttachMode = !!attachToOrder;

  /** Cart view the guards should reason about: unchanged outside attach mode,
   *  new-selection-only inside it. */
  const getGuardCart = useCallback(
    (cart: any) =>
      isAttachMode
        ? { ...cart, products: filterNewCartProducts(cart?.products) }
        : cart,
    [isAttachMode]
  );

  return { isAttachMode, attachToOrder, getGuardCart };
};

export default useAttachMode;
